import { apiRequest } from "@/api/client";
import { getDurableConversation } from "@/api/conversations";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { openDurableConversationFrame } from "@/lib/open-durable-conversation-frame";
import { prewarmAgentSession } from "@/lib/agent-prewarm";
import { useAgentStore } from "@/stores/agent-store";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";
import {
  modelSwitchFrame,
  rebindModelConversationFrame,
} from "./rebind-model-conversation-frame";
import { translate } from "@/i18n";
import { libChatMessages } from "@/i18n/messages/lib-chat";

/**
 * Capture the input bar's chat before saving. On success, a targeted old chat
 * gives way to a pending new chat, which runs on the saved template model.
 */
export function prepareInputBarNewChat(): () => void {
  const { intentId, target } = useInputBarTarget.getState();
  const conversationId = target?.conversationId;
  const isCurrentAuth = captureDesktopChatAuthScopeGuard();
  return () => {
    const route = useInputBarTarget.getState();
    if (
      !isCurrentAuth() ||
      !conversationId ||
      route.target?.conversationId !== conversationId ||
      route.intentId !== intentId
    ) {
      return;
    }
    route.requestNewConversation();
  };
}

/** Capture the chat before saving, then create a separate empty chat on success. */
function prepareNewConversation(
  agentId: string,
  frameId?: string,
  frameLocal = false,
): () => Promise<string | undefined> {
  const projectId = useProjectStore.getState().currentProject?.id;
  const intentId = useInputBarTarget.getState().intentId;
  const isCurrentAuth = captureDesktopChatAuthScopeGuard();
  const frame = frameId ? modelSwitchFrame(frameId) : null;
  const originalConversationId =
    frame?.metadata.conversationId ?? frame?.metadata.conversation_id;
  let createdId: string | undefined;
  const isCurrent = () => {
    const route = useInputBarTarget.getState();
    const currentFrame = frameId ? modelSwitchFrame(frameId) : null;
    const frameConversationId =
      currentFrame?.metadata.conversationId ??
      currentFrame?.metadata.conversation_id;
    return (
      isCurrentAuth() &&
      useProjectStore.getState().currentProject?.id === projectId &&
      (!frameId ||
        (!!frame &&
          !!currentFrame &&
          (frameConversationId === originalConversationId ||
            (!!createdId && frameConversationId === createdId)))) &&
      (frameLocal ||
        route.intentId === intentId ||
        (!!createdId && route.target?.conversationId === createdId))
    );
  };
  return async () => {
    if (!projectId || !isCurrent())
      throw new Error(translate(libChatMessages, "activeChatChanged"));
    const response = await apiRequest<{ data: { id: string } }>(
      `/api/v1/projects/${projectId}/conversations/`,
      {
        method: "POST",
        body: { project_agent_id: agentId, title: "New chat" },
      },
    );
    createdId = response.data.id;
    window.dispatchEvent(new Event("denker-conversations-changed"));
    if (!isCurrent()) return;
    const conversation = await getDurableConversation(createdId);
    if (!isCurrent()) return;
    if (frame) {
      await rebindModelConversationFrame(frame, conversation, isCurrent);
    } else
      await openDurableConversationFrame({
        conversation,
        addToast: useNotificationStore.getState().addSystemToast,
        closePopover: () => {},
        selectAgent: useAgentStore.getState().selectAgent,
        shouldContinue: isCurrent,
      });
    if (
      isCurrent() &&
      (frameLocal
        ? modelSwitchFrame(frameId!)?.metadata.conversationId === createdId
        : useInputBarTarget.getState().target?.conversationId === createdId)
    ) {
      prewarmAgentSession("model_changed", { agentId, force: true });
      return createdId;
    }
  };
}

export function prepareModelConversation(agentId: string, frameId?: string) {
  return prepareNewConversation(agentId, frameId);
}

/** Capture a frame's identity; creation survives the standalone bar's routing
 *  changing meanwhile, and the bar then follows the frame's new chat. */
export function prepareFrameNewConversation(agentId: string, frameId: string) {
  return prepareNewConversation(agentId, frameId, true);
}
